import { BroadcastChannel } from 'broadcast-channel';
import type { Message } from 'shared/domain/messages/message';
import { isPlainObject } from 'shared/utils/object';
import type { AnyChannelContract } from './contract';
import { deliverChannelMessage } from './deliverChannelMessage';
import type { ChannelMessageHandler, ChannelUnsubscribe } from './types';

/** The only `BroadcastChannel` of the app; every contract's messages travel on it. */
export const CHANNEL_BUS_NAME = 'hustro-bus';

/** What travels on the bus: a message of the contract with this key. */
export interface ChannelEnvelope {
  contract: string;
  message: unknown;
}

export function isChannelEnvelope(value: unknown): value is ChannelEnvelope {
  return isPlainObject(value) && typeof value.contract === 'string' && 'message' in value;
}

/** The part of a `BroadcastChannel` the bus uses; replaceable in tests. */
export interface BroadcastPort {
  postMessage(message: unknown): unknown;
  onmessage: ((message: unknown) => void) | null;
  close(): unknown;
}

export interface ChannelBus {
  /** Sends a message of the contract to every other context (tabs, service worker) and to this one. */
  post(contract: AnyChannelContract, message: unknown): void;
  /** Receives the messages of the contract. Returns an idempotent unsubscribe. */
  subscribe(contract: AnyChannelContract, handler: ChannelMessageHandler): ChannelUnsubscribe;
  /** Subscriber count per contract key, for debugging. */
  describeSubscriptions(): Record<string, number>;
  /** Closes the channel and drops every subscription (logout, tests). */
  closeAll(): void;
}

export interface ChannelBusOptions {
  openPort?: (name: string) => BroadcastPort;
  /** A failing handler is reported here and does not stop the others. */
  onHandlerError?: (contractKey: string, error: unknown, message: Message) => void;
  /** A message the channel could not send (e.g. it cannot be cloned); the sender is not interrupted. */
  onPostError?: (contractKey: string, error: unknown, message: unknown) => void;
}

/** What a message is, for logs: its event `type` or request `kind`. */
export function describeChannelMessage(message: unknown): string {
  const { type, kind } = (message ?? {}) as { type?: unknown; kind?: unknown };
  return String(type ?? kind ?? 'unknown');
}

/** Keyed per subscription, so one handler subscribed twice is released twice. */
type Subscriptions = Map<symbol, ChannelMessageHandler>;

/**
 * One long-lived `BroadcastChannel` for the whole context (page or service
 * worker), shared by every contract: each message is wrapped with its
 * contract's key and delivered only to that contract's subscribers. A channel
 * does not receive its own messages, so the bus delivers what this context
 * posts to its own subscribers too.
 */
export function createChannelBus({
  openPort = (name): BroadcastPort => new BroadcastChannel(name),
  onHandlerError = (): void => undefined,
  onPostError = (): void => undefined,
}: ChannelBusOptions = {}): ChannelBus {
  const subscriptionsByContract = new Map<string, Subscriptions>();
  let port: BroadcastPort | undefined;

  const deliver = (contractKey: string, message: unknown): void => {
    const subscriptions = subscriptionsByContract.get(contractKey);
    if (!subscriptions?.size) {
      return;
    }
    deliverChannelMessage(subscriptions.values(), message as Message, (error) =>
      onHandlerError(contractKey, error, message as Message)
    );
  };

  const openedPort = (): BroadcastPort => {
    if (!port) {
      port = openPort(CHANNEL_BUS_NAME);
      port.onmessage = (envelope): void => {
        if (isChannelEnvelope(envelope)) {
          deliver(envelope.contract, envelope.message);
        }
      };
    }
    return port;
  };

  return {
    post({ key }, message) {
      const envelope: ChannelEnvelope = { contract: key, message };
      try {
        void openedPort().postMessage(envelope);
      } catch (error) {
        onPostError(key, error, message);
        return;
      }
      // Asynchronous like a broadcast: a subscriber added right after posting still gets it.
      queueMicrotask(() => deliver(key, message));
    },

    subscribe({ key }, handler) {
      openedPort();
      const subscriptions = subscriptionsByContract.get(key) ?? new Map();
      subscriptionsByContract.set(key, subscriptions);
      const subscriptionId = Symbol(key);
      subscriptions.set(subscriptionId, handler);
      return () => {
        subscriptions.delete(subscriptionId);
      };
    },

    describeSubscriptions() {
      return Object.fromEntries(
        [...subscriptionsByContract].map(([contractKey, subscriptions]) => [contractKey, subscriptions.size])
      );
    },

    closeAll() {
      subscriptionsByContract.forEach((subscriptions) => subscriptions.clear());
      subscriptionsByContract.clear();
      void port?.close();
      port = undefined;
    },
  };
}
